// Génération des blocs JSON-LD (schema.org), pilotée par src/content/meta.js.
//
// Module PUR (aucune dépendance au DOM/window) afin d'être importable à la fois :
//  - côté client par src/components/LegacyPage.jsx (injection dans <head> après montage)
//  - côté build par scripts/prerender.mjs (Node, génération du HTML statique)
// Les deux consommateurs produisent ainsi un JSON-LD strictement identique.

import { SITE_URL, SITE_NAME, SITE_ENTITY_DESCRIPTION, ORGANIZATION_SAME_AS, FOUNDERS, absoluteUrl } from './site.js';

const ORG_ID = `${SITE_URL}/#organization`;

export function buildOrganization() {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    legalName: SITE_NAME,
    url: SITE_URL,
    description: SITE_ENTITY_DESCRIPTION,
    ...(ORGANIZATION_SAME_AS.length > 0 ? { sameAs: ORGANIZATION_SAME_AS } : {}),
    founder: FOUNDERS.map((founder) => ({
      '@type': 'Person',
      name: founder.name,
      jobTitle: founder.jobTitle,
    })),
  };
}

export function buildSoftwareApplication(page, meta) {
  const node = {
    '@type': 'SoftwareApplication',
    '@id': `${absoluteUrl(page.path)}#software`,
    name: SITE_NAME,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: absoluteUrl(page.path),
    description: meta.description,
    provider: { '@id': ORG_ID },
  };
  if (meta.offers?.length) {
    node.offers = meta.offers.map((offer) => ({
      '@type': 'Offer',
      name: offer.name,
      price: offer.price,
      priceCurrency: offer.priceCurrency,
      url: absoluteUrl(page.path),
    }));
  }
  return node;
}

export function buildBreadcrumbList(meta) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: meta.breadcrumb.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

export function buildFAQPage(meta) {
  return {
    '@type': 'FAQPage',
    mainEntity: meta.faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };
}

export function buildBlogPosting(page, meta) {
  const node = {
    '@type': 'BlogPosting',
    '@id': `${absoluteUrl(page.path)}#article`,
    headline: meta.h1 || meta.title,
    description: meta.description,
    url: absoluteUrl(page.path),
    mainEntityOfPage: absoluteUrl(page.path),
    inLanguage: 'fr-FR',
    isPartOf: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
  };
  if (meta.author) {
    node.author = { '@type': 'Person', name: meta.author.name, jobTitle: meta.author.jobTitle };
  }
  if (meta.readingTime) {
    node.timeRequired = readingTimeToISO8601(meta.readingTime);
  }
  return node;
}

export function buildBlogIndex(page, meta, posts) {
  return {
    '@type': 'CollectionPage',
    name: meta.title,
    url: absoluteUrl(page.path),
    description: meta.description,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: posts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: absoluteUrl(post.path),
        name: post.title,
      })),
    },
  };
}

function readingTimeToISO8601(label) {
  const minutes = parseInt(label, 10);
  return Number.isFinite(minutes) ? `PT${minutes}M` : undefined;
}

// Construit le graphe JSON-LD complet d'une page à partir de sa config `schemas`
// (voir src/content/meta.js). `blogPosts` doit être fourni pour la page /blog.
export function getJsonLdGraph(page, meta, { blogPosts } = {}) {
  if (!meta) return null;
  const schemas = meta.schemas || [];
  const graph = [];

  if (schemas.includes('organization')) graph.push(buildOrganization());
  if (schemas.includes('softwareApplication')) graph.push(buildSoftwareApplication(page, meta));
  if (schemas.includes('breadcrumb') && meta.breadcrumb?.length > 1) graph.push(buildBreadcrumbList(meta));
  if (schemas.includes('faq') && meta.faq?.length) graph.push(buildFAQPage(meta));
  if (schemas.includes('blogPosting')) graph.push(buildBlogPosting(page, meta));
  if (schemas.includes('blogIndex')) graph.push(buildBlogIndex(page, meta, blogPosts || []));

  if (!graph.length) return null;
  return { '@context': 'https://schema.org', '@graph': graph };
}
